import { List, Map } from 'immutable';

import { toSeverityCounts, type AlarmSeverity } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';

export interface UnitAlarmCount {
  severity: AlarmSeverity;
  count: number;
  /** The client's own criticality label. */
  label: string;
}

export interface UnitAlarms {
  unit: string;
  counts: UnitAlarmCount[];
}

export const UNASSIGNED_UNIT = '';

/**
 * Builds alarm counts for every unit, including units with no alarms. Sorted by
 * severity counts descending, then by unit name.
 *
 * @param {string[]} units all unit names
 * @param {Map} summaryByUnit alarm counts keyed by unit, then criticality level
 * @param {List} criticalityLabels the client's criticality labels
 * @returns {UnitAlarms[]} one entry per unit, most alarming first
 */
export function toUnitAlarms (
  units: string[],
  summaryByUnit: Map<string, Map<string, number>>,
  criticalityLabels: List<string>
): UnitAlarms[] {
  const tiles = units.map((unit) => ({
    unit,
    counts: toSeverityCounts((summaryByUnit.get(unit) || Map()).toJS(), criticalityLabels),
  }));

  return tiles.sort((a, b) => {
    for (let i = 0; i < a.counts.length; i++) {
      const diff = (b.counts[i]?.count ?? 0) - (a.counts[i]?.count ?? 0);
      if (diff) return diff;
    }

    return a.unit.localeCompare(b.unit);
  });
}

/**
 * The severity slots with alarms on devices that have no unit, so the tiles
 * plus these add up to Alarms to Triage. Empty when the facility has no units:
 * the card shows its empty state then, and this would just repeat the triage
 * card.
 *
 * @param {string[]} units all unit names
 * @param {Map} summaryByUnit alarm counts keyed by unit, then criticality level
 * @param {List} criticalityLabels the client's criticality labels
 * @returns {UnitAlarmCount[]} the non-zero slots, most severe first
 */
export function toUnassignedCounts (
  units: string[],
  summaryByUnit: Map<string, Map<string, number>>,
  criticalityLabels: List<string>
): UnitAlarmCount[] {
  if (!units.length) return [];

  return toSeverityCounts((summaryByUnit.get(UNASSIGNED_UNIT) || Map()).toJS(), criticalityLabels)
    .filter(({ count }) => count > 0);
}
